import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import './App.css';

// Components
import LoginStable from './components/LoginStable';
import Dashboard from './components/Dashboard';
import ProductionWorking from './components/ProductionWorking';
import SalesEntry from './components/SalesEntry';
import ReturnsEntry from './components/ReturnsEntry';
import StockEntry from './components/StockEntry';
import ExpenseEntryFixed from './components/ExpenseEntryFixed';
import SimpleUsersPage from './components/SimpleUsersPage';
import UsersPageWorking from './components/UsersPageWorking';
import ViennoiserieProduction from './components/ViennoiserieProduction';
import ApprovalsPageMinimal from './components/ApprovalsPageMinimal';
import SimpleBackupPage from './components/SimpleBackupPage';
import Reports from './components/Reports';
import InvoiceManagement from './components/InvoiceManagement';
import CompanySetup from './components/CompanySetup';
import CompanySettings from './components/CompanySettings';
import AuditTrail from './components/AuditTrail';
import ProtectedRoute from './components/ProtectedRoute';
import { Toaster } from './components/ui/sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Context pour l'utilisateur connecté
const UserContext = React.createContext();

// Context pour l'entreprise
const CompanyContext = React.createContext();

function App() {
  const [user, setUser] = useState(null);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [requiresCompany, setRequiresCompany] = useState(false);
  const [checkingCompany, setCheckingCompany] = useState(false);
  
  useEffect(() => {
    // Vérifier si un utilisateur est déjà connecté
    const savedUser = localStorage.getItem('user');
    if (savedUser) {
      const userData = JSON.parse(savedUser);
      setUser(userData);
      // Vérifier l'entreprise pour cet utilisateur
      checkCompanyRequirement(userData.id);
    }
    setLoading(false);
  }, []);

  const handleLogin = async (username, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, {
        username,
        password
      });
      
      const userData = response.data.user;
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
      
      // Vérifier si l'entreprise est requise
      await checkCompanyRequirement(userData.id);
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Erreur de connexion' 
      };
    }
  };

  const checkCompanyRequirement = async (userId) => {
    try {
      setCheckingCompany(true);
      const response = await axios.get(`${API}/company/check-required/${userId}`);
      setRequiresCompany(response.data.requires_company);
      
      // Si l'entreprise existe, la charger
      if (!response.data.requires_company && response.data.has_company) {
        const companyResponse = await axios.get(`${API}/company/user/${userId}`);
        setCompany(companyResponse.data);
      }
    } catch (error) {
      console.error('Error checking company requirement:', error);
      setRequiresCompany(false);
      setCompany(null);
    } finally {
      setCheckingCompany(false);
    }
  };

  const handleCompanyCreated = async () => {
    setRequiresCompany(false);
    // Recharger les informations de l'entreprise
    if (user) {
      try {
        const companyResponse = await axios.get(`${API}/company/user/${user.id}`);
        setCompany(companyResponse.data);
      } catch (error) {
        console.error('Error loading company after creation:', error);
      }
    }
  };

  const updateCompany = (updatedCompany) => {
    setCompany(updatedCompany);
  };

  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem('user');
  };

  if (loading || checkingCompany) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 to-amber-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">
            {loading ? 'Chargement...' : 'Vérification de l\'entreprise...'}
          </p>
        </div>
      </div>
    );
  }

  // Afficher la création d'entreprise si requise
  if (user && requiresCompany) {
    return (
      <UserContext.Provider value={{ user, handleLogin, handleLogout, checkCompanyRequirement, handleCompanyCreated, API }}>
        <CompanyContext.Provider value={{ company, updateCompany }}>
          <div className="App">
            <CompanySetup onCompanyCreated={handleCompanyCreated} />
            <Toaster />
          </div>
        </CompanyContext.Provider>
      </UserContext.Provider>
    );
  }

  return (
    <UserContext.Provider value={{ user, handleLogin, handleLogout, checkCompanyRequirement, handleCompanyCreated, API }}>
      <CompanyContext.Provider value={{ company, updateCompany }}>
        <div className="App">
          <BrowserRouter>
            <Routes>
            <Route 
              path="/login" 
              element={!user ? <LoginStable /> : <Navigate to="/dashboard" />} 
            />
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute user={user} allowedRoles={['staff', 'manager', 'owner', 'system_admin']}>
                  <Dashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/stock" 
              element={
                <ProtectedRoute user={user} allowedRoles={['staff', 'manager', 'owner', 'system_admin']}>
                  <StockEntry />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/production" 
              element={
                <ProtectedRoute user={user} allowedRoles={['staff', 'manager', 'owner', 'system_admin']}>
                  <ProductionWorking />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/production/viennoiserie" 
              element={
                <ProtectedRoute user={user} allowedRoles={['staff', 'manager', 'owner', 'system_admin']}>
                  <ViennoiserieProduction />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/sales" 
              element={
                <ProtectedRoute user={user} allowedRoles={['manager', 'owner', 'system_admin']}>
                  <SalesEntry />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/returns" 
              element={
                <ProtectedRoute user={user} allowedRoles={['staff', 'manager', 'owner', 'system_admin']}>
                  <ReturnsEntry />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/expenses" 
              element={
                <ProtectedRoute user={user} allowedRoles={['manager', 'owner', 'system_admin']}>
                  <ExpenseEntryFixed />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/reports" 
              element={
                <ProtectedRoute user={user} allowedRoles={['staff', 'manager', 'owner', 'system_admin']}>
                  <Reports />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/invoices" 
              element={
                <ProtectedRoute user={user} allowedRoles={['manager', 'owner', 'system_admin']}>
                  <InvoiceManagement />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/users" 
              element={<UsersPageWorking />} 
            />
            <Route 
              path="/users/pending" 
              element={
                <ProtectedRoute user={user} allowedRoles={['system_admin']}>
                  <ApprovalsPageMinimal />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/system/backup" 
              element={
                <ProtectedRoute user={user} allowedRoles={['system_admin']}>
                  <SimpleBackupPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/system/restore" 
              element={
                <ProtectedRoute user={user} allowedRoles={['system_admin']}>
                  <SimpleBackupPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/company/settings" 
              element={
                <ProtectedRoute user={user} allowedRoles={['owner', 'system_admin']}>
                  <CompanySettings />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/company/create" 
              element={
                <ProtectedRoute user={user} allowedRoles={['owner', 'system_admin']}>
                  <CompanySetup onCompanyCreated={handleCompanyCreated} />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/system/audit-trail-secret" 
              element={
                <ProtectedRoute user={user} allowedRoles={['owner', 'system_admin']}>
                  <AuditTrail />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/" 
              element={<Navigate to={user ? "/dashboard" : "/login"} />} 
            />
          </Routes>
        </BrowserRouter>
        <Toaster />
      </div>
      </CompanyContext.Provider>
    </UserContext.Provider>
  );
}

export { UserContext, CompanyContext };
export default App;